import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    useTheme,
    Skeleton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { listClients, ClientListItem } from '../../services/clients-service';
import { formatFriendlyDate } from '../../utils/dateUtils';
import AddClientPanel from './AddClientPanel';

const ClientListPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [clients, setClients] = useState<ClientListItem[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [addPanelOpen, setAddPanelOpen] = useState(false);

    const loadClients = () => {
        setLoading(true);
        setError(null);
        listClients()
            .then(setClients)
            .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load clients'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadClients();
    }, []);

    const handleRowClick = (clientId: number) => {
        navigate(`/admin/clients/${clientId}`);
    };

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary }}>
                    Clients
                </Typography>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                    Clients
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setAddPanelOpen(true)}
                    sx={{ textTransform: 'none' }}
                >
                    Create client
                </Button>
            </Box>

            <AddClientPanel
                open={addPanelOpen}
                onClose={() => setAddPanelOpen(false)}
                onSuccess={() => loadClients()}
            />

            <TableContainer component={Paper} sx={{ bgcolor: theme.palette.background.paper }}>
                <Table size="medium">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>Subdomain</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>Contact</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>Created</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            Array.from({ length: 5 }).map((_, i) => (
                                <TableRow key={i}>
                                    <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                                    <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                                    <TableCell><Skeleton variant="text" width={70} /></TableCell>
                                    <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                                    <TableCell><Skeleton variant="text" width={90} /></TableCell>
                                </TableRow>
                            ))
                        ) : clients.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 4, color: theme.palette.text.secondary }}>
                                    No clients found
                                </TableCell>
                            </TableRow>
                        ) : (
                            clients.map((row) => (
                                <TableRow
                                    key={row.clientId}
                                    hover
                                    sx={{
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: theme.palette.action.hover },
                                    }}
                                    onClick={() => handleRowClick(row.clientId)}
                                >
                                    <TableCell sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                                        {row.clientName}
                                    </TableCell>
                                    <TableCell sx={{ color: theme.palette.text.secondary }}>
                                        {row.subdomain}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.isActive ? 'Active' : 'Paused'}
                                            size="small"
                                            sx={{
                                                bgcolor: row.isActive
                                                    ? theme.palette.success.main + '20'
                                                    : theme.palette.text.secondary + '30',
                                                color: row.isActive
                                                    ? theme.palette.success.dark
                                                    : theme.palette.text.secondary,
                                                fontWeight: 500,
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ color: theme.palette.text.secondary }}>
                                        {row.contactEmail ?? '—'}
                                    </TableCell>
                                    <TableCell sx={{ color: theme.palette.text.secondary }}>
                                        {row.recordCreateDate ? formatFriendlyDate(row.recordCreateDate) : '—'}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default ClientListPage;
